import React, { useState, useEffect, useRef } from 'react';

import Button from '@atoms/new/button';
import Input from '@atoms/Input';
import Checkbox from '@atoms/Checkbox.new';
import Banner from '@components/Presentation/atoms/Banner';
import { SelectItem, Select } from '@atoms/new/select';
import MaxLengthCounter from '@components/Presentation/atoms/MaxLengthCounter';

import SettingBlock from '@components/Presentation/main/Settings/units/SettingBlock';
import OptionOrderType from '@components/Presentation/main/Settings/units/OptionOrderType';

import { calculateRemainingChars } from '@components/Presentation/helpers';
import { OPTIONS_LABEL } from '@services/interactions.service';
import Switch from '@atoms/new/switch';
import {
  PlusIcon as AddRounded,
  XIcon as CloseRounded,
} from '@phosphor-icons/react';

const QuizOptions = ({
  options,
  setting,
  updateSetting,
  handleChangeOptionBody,
  handleChangeOptionCheck,
  addNewOption,
  deleteOption
}) => {
  const [isOptionFocused, setIsOptionFocused] = useState(undefined);
  const [errorMsg, setErrorMsg] = useState('');

  const optionsRef = useRef(null);

  const isAnyOptionChecked = options?.some(
    (option) => option.checked
  );

  useEffect(() => {
    if (options.length > 2) {
      optionsRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [options.length]);

  const handleMinPointsChange = (e) => {
    const value = Number(e.target.value.replace(/\D/g, ''));
    if (value >= setting?.quizMaxPoints) {
      setErrorMsg('Min value should be smaller than max.');
    } else {
      setErrorMsg('');
    }
    updateSetting('quizMinPoints', value);
  };

  return (
    <>
      <SettingBlock
        title="Options"
        tooltip="Add options for your audience to vote on."
        hasBorderTop={true}
        addPadding={false}
      >
        <div className="flex w-full items-center gap-3  mt-1">
          <p className="font-inter text-sm text-default-900 font-medium flex flex-1">Label:</p>
          <div className="flex  gap-x-2">
            <Select
              label=""
              aria-label="Label"
              className="min-w-[104px]"
              selectedKeys={[setting.optionOrderType || 'number']}
              onChange={(e) => e.target.value && updateSetting('optionOrderType', e.target.value)}
            >
              {OPTIONS_LABEL.map((option) => (
                <SelectItem key={option.key}>
                  {option.label}
                </SelectItem>
              ))}
            </Select>
          </div>
        </div>
        <p className="font-inter text-sm text-default-900 font-medium">List:</p>
        {options?.map((option, index) => (
          <div key={option.key || option.id}>
            <div className="flex gap-2  items-center">
              <Checkbox
                checked={!!option.checked}
                size="lg"
                onChange={() => {
                  if (option.checked) return;
                  const prevIndex = options.findIndex(option => option.checked === true);
                  const e = { target: { value: !option.checked } };
                  handleChangeOptionCheck(index, e, prevIndex > -1 ? prevIndex : undefined);
                }}
              />

              <OptionOrderType
                index={index}
                type={setting.optionOrderType || 'number'}
              />

              <Input
                placeholder={`Option ${index + 1}`}
                value={option.body}
                onChange={(e) => handleChangeOptionBody(index, e)}
                handleFocus={(v) => setIsOptionFocused(v ? index : undefined)}
                size="lg"
                maxLength={60}
                addOnRight={
                  isOptionFocused === index ? (
                    <MaxLengthCounter
                      threshold={10}
                      value={calculateRemainingChars(option.body, 60)}
                    />
                  ) : null
                }
              />

              <Button
                kind="tertiary"
                size="md"
                onClick={() => deleteOption(option)}
                isDisabled={options.length <= 2 && (index === 0 || index === 1)}
                isIconOnly
              >
                <CloseRounded size={20} />
              </Button>
            </div>
            {calculateRemainingChars(option.body, 60) === 0 && (
              <p className="text-sm text-alert mt-1">You have reached the maximum length</p>
            )}
          </div>
        ))}
        <Button
          ref={optionsRef}
          kind="tertiary"
          size="sm"
          onClick={addNewOption}
          fullWidth
          startContent={<AddRounded size={20} />}
          isDisabled={options.length >= 4}
        >
        Add option
        </Button>
        <Banner
          open={!isAnyOptionChecked}
          key={'2'}
          content="Choose one correct option"
        />
      </SettingBlock>
      <SettingBlock
        title="Think fast"
        tooltip="A timer will count down during the quiz."
        hasBorderTop={true}
        titleMargin="sm"
        addPadding={false}
      >
        <div className='text-sm flex items-center justify-between'>
          {setting?.enableQuizResponseTime ? (
            <>
              <span>Points</span>
              <div className='flex w-[292px] gap-2'>
                <Input
                  inputMode="numeric"
                  pattern="\d*"
                  maxLength={5}
                  value={String(setting.quizMaxPoints ?? 1000)}
                  onChange={(e) => updateSetting('quizMaxPoints', Number(e.target.value.replace(/\D/g, '')))}
                  addOnLeft={<span className='pl-1 text-grey-700'>Max:</span>} />
                <Input
                  inputMode="numeric"
                  pattern="\d*"
                  maxLength={5}
                  value={String(setting.quizMinPoints ?? 200)}
                  onChange={handleMinPointsChange}
                  addOnLeft={<span className='pl-1 text-grey-700'>Min:</span>} />
              </div>
            </>
          ) : (
            <>
              <span>Points</span>
              <div className="flex w-[96px] gap-2">
                <Input
                  inputMode="numeric"
                  pattern="\d*"
                  maxLength={5}
                  value={String(setting.quizMaxPoints ?? 1000)}
                  onChange={(e) => {
                    const value = Number(e.target.value.replace(/\D/g, ''));
                    updateSetting('quizMaxPoints', value);
                    updateSetting('quizMinPoints', 0);
                  }}
                />
              </div>
            </>
          )}
        </div>
        <Banner
          open={!!errorMsg}
          key={'3'}
          content={errorMsg}
        />
        <div className='text-sm flex items-center justify-between'>
          <span>Faster responses, more points.</span>
          <Switch
            disableAnimation
            isSelected={setting?.enableQuizResponseTime ?? false}
            onChange={(e) => {
              updateSetting('enableQuizResponseTime', e.target.checked);
            }}
          />
        </div>
        <div className='text-sm flex items-center justify-between'>
          <span>Timer</span>
          <div className='flex w-[112px]'>
            <Input
              inputMode="numeric"
              pattern="\d*"
              maxLength={3}
              value={String(setting.quizTimerDuration ?? 20)}
              onChange={(e) => {
                const value = Number(e.target.value.replace(/\D/g, ''));
                updateSetting('quizTimerDuration', value);
              }}
              addOnRight={<span className="pr-1 text-grey-500 font-normal text-sm">seconds</span>}
            />
          </div>
        </div>
      </SettingBlock>
      <SettingBlock
        title="Sound"
        hasBorderTop={true}
        titleMargin="sm"
        addPadding={false}
      >
        <div className='text-sm flex items-center justify-between'>
          <span>Enable quiz sound</span>
          <Switch
            disableAnimation
            isSelected={setting?.isQuizSoundEnabled ?? true}
            onChange={(e) => {
              updateSetting('isQuizSoundEnabled', e.target.checked);
            }}
          />
        </div>
      </SettingBlock>
    </>
  );
};

export default QuizOptions;
